/**
 * Aviso de reposición (Klaviyo Back in Stock) — De la Mur
 *
 * Se apoya en el endpoint público de cliente de Klaviyo, que solo necesita el
 * company_id (clave pública). No se expone ninguna clave privada.
 * https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
 *
 * El componente va dentro del formulario de producto, así que no tiene <form> propio: el envío
 * sale del botón o de Enter (que se intercepta para que no añada el producto al carrito).
 * Escucha `pdp:variant-change`, que emite component-pdp.js al cambiar de variante.
 */
(() => {
  const ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const REVISION = '2026-07-15';
  const VARIANT_UPDATE_EVENT = 'pdp:variant-change';

  class KlaviyoBackInStock extends HTMLElement {
    #abortController = new AbortController();

    connectedCallback() {
      const { signal } = this.#abortController;

      this.companyId = this.dataset.companyId;
      this.variantId = this.dataset.variantId;

      this.trigger = this.querySelector('[data-kbis-trigger]');
      this.panel = this.querySelector('[data-kbis-panel]');
      this.submitBtn = this.querySelector('[data-kbis-submit]');
      this.input = this.querySelector('[data-kbis-email]');
      this.messageEl = this.querySelector('[data-kbis-message]');
      this.variantEl = this.querySelector('[data-kbis-variant]');

      this.trigger?.addEventListener('click', () => this.openPanel(), { signal });
      this.submitBtn?.addEventListener('click', (event) => this.handleSubmit(event), { signal });
      this.input?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') this.handleSubmit(event);
      }, { signal });
      document.addEventListener(VARIANT_UPDATE_EVENT, (event) => this.handleVariantUpdate(event), { signal });
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }

    /**
     * El tema emite `variant:update` con la variante ya resuelta. `resource` es
     * null cuando la combinación de opciones no existe: en ese caso no hay nada
     * a lo que suscribirse, así que nos ocultamos.
     */
    handleVariantUpdate(event) {
      const detail = event.detail || {};
      const variant = detail.variant;

      if (!variant) {
        this.hide();
        return;
      }

      const variantChanged = String(variant.id) !== String(this.variantId);
      this.variantId = String(variant.id);

      if (variantChanged) this.reset();

      if (variant.available) {
        this.hide();
      } else {
        this.show(variant);
      }
    }

    show(variant) {
      this.hidden = false;

      // El título de la variante solo aporta si el producto tiene más de una.
      if (this.variantEl && variant && variant.title && this.dataset.hasVariants === 'true') {
        this.variantEl.textContent = variant.title;
      }
    }

    hide() {
      this.hidden = true;
      this.closePanel();
    }

    openPanel() {
      if (!this.panel) return;
      this.panel.hidden = false;
      this.trigger?.setAttribute('aria-expanded', 'true');
      this.input?.focus();
    }

    closePanel() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.trigger?.setAttribute('aria-expanded', 'false');
    }

    /** Vuelve al estado inicial al cambiar de variante. */
    reset() {
      this.removeAttribute('data-state');
      this.closePanel();
      this.setMessage('');
      if (this.input) this.input.value = '';
    }

    setMessage(text, kind) {
      if (!this.messageEl) return;

      this.messageEl.textContent = text;
      this.messageEl.hidden = !text;
      this.messageEl.classList.toggle('kbis__message--error', kind === 'error');
      this.messageEl.classList.toggle('kbis__message--success', kind === 'success');
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (this.getAttribute('data-state') === 'loading') return;

      const email = (this.input?.value || '').trim();

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.setMessage(this.dataset.textInvalid, 'error');
        this.input?.focus();
        return;
      }

      this.setAttribute('data-state', 'loading');
      this.setMessage(this.dataset.textSending);

      try {
        const response = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(this.companyId)}`, {
          method: 'POST',
          headers: {
            'content-type': 'application/vnd.api+json',
            revision: REVISION,
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email },
                  },
                },
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${this.variantId}`,
                  },
                },
              },
            },
          }),
        });

        if (!response.ok) throw new Error(`Klaviyo respondió ${response.status}`);

        this.setAttribute('data-state', 'done');
        this.setMessage(this.dataset.textSuccess, 'success');
      } catch (error) {
        console.error('[back-in-stock]', error);
        this.removeAttribute('data-state');
        this.setMessage(this.dataset.textError, 'error');
      }
    }
  }

  if (!customElements.get('klaviyo-back-in-stock')) {
    customElements.define('klaviyo-back-in-stock', KlaviyoBackInStock);
  }
})();
